import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Check, Pencil, Plus, Trash2, X } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { AFFILIATE_CATEGORIES } from "@/lib/affiliates";
import AffiliateAdminDetail from "./AffiliateAdminDetail";
import AffiliatePerformanceTable from "./AffiliatePerformanceTable";

interface Affiliate {
  id?: string;
  slug?: string;
  name: string;
  tagline?: string | null;
  description?: string | null;
  logo_url?: string | null;
  website?: string | null;
  contact_email?: string | null;
  location?: string | null;
  category: "freelance" | "sourcing_production";
  services: string[];
  specialties?: string[];
  status: "pending" | "active" | "paused";
  featured: boolean;
  show_contact_email_public?: boolean;
}

interface Application {
  id: string;
  applicant_name: string;
  email: string;
  organization: string | null;
  category: string;
  services: string[];
  portfolio_url: string | null;
  pitch: string;
  location: string | null;
  status: string;
  created_at: string;
}

interface Lead {
  id: string;
  affiliate_id: string;
  name: string;
  email: string;
  organization: string | null;
  project_summary: string;
  budget_range: string | null;
  timeline: string | null;
  status: string;
  created_at: string;
  updated_at?: string | null;
  notes?: string | null;
  affiliates?: { name: string; slug: string } | null;
}

const emptyAffiliate: Affiliate = {
  name: "",
  tagline: "",
  description: "",
  logo_url: "",
  website: "",
  contact_email: "",
  location: "",
  category: "freelance",
  services: [],
  specialties: [],
  status: "active",
  featured: false,
  show_contact_email_public: false,
};

const AffiliatesAdminTab = () => {
  const [affiliates, setAffiliates] = useState<Affiliate[]>([]);
  const [applications, setApplications] = useState<Application[]>([]);
  const [leads, setLeads] = useState<Lead[]>([]);
  const [editing, setEditing] = useState<Affiliate | null>(null);
  const [detailId, setDetailId] = useState<string | null>(null);

  const call = async (action: string, payload?: any) => {
    const { data, error } = await supabase.functions.invoke("manage-affiliates", {
      body: { action, payload },
    });
    if (error) throw new Error(error.message);
    if (data?.error) throw new Error(data.error);
    return data;
  };

  const loadAll = async () => {
    try {
      const [a, app, l] = await Promise.all([
        call("list_affiliates"),
        call("list_applications"),
        call("list_leads"),
      ]);
      setAffiliates(a.affiliates ?? []);
      setApplications(app.applications ?? []);
      setLeads(l.leads ?? []);
    } catch (e: any) {
      toast.error(e.message);
    }
  };

  useEffect(() => { loadAll(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    if (!editing?.name) { toast.error("Name is required"); return; }
    try {
      await call("upsert_affiliate", {
        ...editing,
        services: editing.services ?? [],
        specialties: editing.specialties ?? [],
      });
      toast.success("Saved");
      setEditing(null);
      loadAll();
    } catch (e: any) { toast.error(e.message); }
  };

  const remove = async (id?: string) => {
    if (!id || !confirm("Delete this affiliate?")) return;
    try { await call("delete_affiliate", { id }); toast.success("Deleted"); loadAll(); }
    catch (e: any) { toast.error(e.message); }
  };

  const approve = async (id: string) => {
    try {
      const data = await call("approve_application", { id });
      if (data?.linkedToAccount) {
        toast.success("Approved — listed in directory and applicant granted Partner Hub access");
      } else {
        toast.warning(
          "Approved & listed, but applicant has no account yet. They must sign up — re-approve (or use the Promote action) afterward to grant Partner Hub access.",
          { duration: 8000 },
        );
      }
      loadAll();
    } catch (e: any) {
      toast.error(e.message);
    }
  };

  const setAppStatus = async (id: string, status: string) => {
    try { await call("update_application", { id, status }); loadAll(); }
    catch (e: any) { toast.error(e.message); }
  };

  const setLeadStatus = async (id: string, status: string) => {
    try { await call("update_lead", { id, status }); loadAll(); }
    catch (e: any) { toast.error(e.message); }
  };

  const updateAffiliate = async (id: string, patch: Partial<Affiliate>) => {
    try {
      const target = affiliates.find((a) => a.id === id);
      if (!target) return;
      await call("upsert_affiliate", { ...target, ...patch });
      loadAll();
    } catch (e: any) {
      toast.error(e.message);
    }
  };

  if (editing) {
    return (
      <div className="space-y-4 max-w-2xl">
        <div className="flex items-center justify-between">
          <h3 className="font-display text-lg font-semibold">{editing.id ? "Edit Affiliate" : "New Affiliate"}</h3>
          <Button variant="ghost" size="sm" onClick={() => setEditing(null)}>Cancel</Button>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <Label>Name</Label>
            <Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} />
          </div>
          <div>
            <Label>Slug (auto if empty)</Label>
            <Input value={editing.slug ?? ""} onChange={(e) => setEditing({ ...editing, slug: e.target.value })} />
          </div>
        </div>
        <div>
          <Label>Tagline</Label>
          <Input value={editing.tagline ?? ""} onChange={(e) => setEditing({ ...editing, tagline: e.target.value })} />
        </div>
        <div>
          <Label>Description</Label>
          <Textarea rows={4} value={editing.description ?? ""} onChange={(e) => setEditing({ ...editing, description: e.target.value })} />
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <Label>Website</Label>
            <Input value={editing.website ?? ""} onChange={(e) => setEditing({ ...editing, website: e.target.value })} />
          </div>
          <div>
            <Label>Contact email</Label>
            <Input value={editing.contact_email ?? ""} onChange={(e) => setEditing({ ...editing, contact_email: e.target.value })} />
          </div>
          <div>
            <Label>Location</Label>
            <Input value={editing.location ?? ""} onChange={(e) => setEditing({ ...editing, location: e.target.value })} />
          </div>
          <div>
            <Label>Logo URL</Label>
            <Input value={editing.logo_url ?? ""} onChange={(e) => setEditing({ ...editing, logo_url: e.target.value })} />
          </div>
        </div>
        <div className="grid sm:grid-cols-3 gap-3">
          <div>
            <Label>Category</Label>
            <Select value={editing.category} onValueChange={(v) => setEditing({ ...editing, category: v as any })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {AFFILIATE_CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Status</Label>
            <Select value={editing.status} onValueChange={(v) => setEditing({ ...editing, status: v as any })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="paused">Paused</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-end gap-3">
            <Switch checked={editing.featured} onCheckedChange={(v) => setEditing({ ...editing, featured: v })} />
            <span className="text-sm">Featured</span>
          </div>
        </div>
        <div className="flex items-center gap-3 rounded-md border border-border p-3">
          <Switch
            checked={!!editing.show_contact_email_public}
            onCheckedChange={(v) => setEditing({ ...editing, show_contact_email_public: v })}
          />
          <div className="text-sm">
            <div>Show contact email publicly</div>
            <div className="text-xs text-muted-foreground">When off, the email is hidden from anonymous visitors on the public directory.</div>
          </div>
        </div>
        <div>
          <Label>Services (comma-separated)</Label>
          <Input
            value={editing.services?.join(", ") ?? ""}
            onChange={(e) => setEditing({ ...editing, services: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })}
          />
        </div>
        <div>
          <Label>Specialties (comma-separated)</Label>
          <Input
            value={editing.specialties?.join(", ") ?? ""}
            onChange={(e) => setEditing({ ...editing, specialties: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })}
          />
        </div>
        <Button onClick={save} className="bg-primary text-primary-foreground">Save Affiliate</Button>
      </div>
    );
  }

  if (detailId) {
    const aff = affiliates.find((a) => a.id === detailId);
    if (!aff) {
      setDetailId(null);
      return null;
    }
    return (
      <AffiliateAdminDetail
        affiliate={aff}
        leads={leads}
        onBack={() => setDetailId(null)}
        onEdit={() => { setEditing(aff); setDetailId(null); }}
        onUpdate={(patch) => updateAffiliate(aff.id!, patch as Partial<Affiliate>)}
        onLeadStatus={setLeadStatus}
      />
    );
  }

  return (
    <Tabs defaultValue="providers">
      <TabsList className="mb-4">
        <TabsTrigger value="providers">Providers ({affiliates.length})</TabsTrigger>
        <TabsTrigger value="applications">
          Applications ({applications.filter((a) => a.status === "new" || a.status === "reviewing").length})
        </TabsTrigger>
        <TabsTrigger value="leads">Leads ({leads.length})</TabsTrigger>
        <TabsTrigger value="performance">Performance</TabsTrigger>
      </TabsList>

      <TabsContent value="providers">
        <div className="flex justify-end mb-4">
          <Button onClick={() => setEditing({ ...emptyAffiliate })} className="bg-primary text-primary-foreground">
            <Plus className="h-4 w-4 mr-2" /> New Affiliate
          </Button>
        </div>
        <div className="space-y-3">
          {affiliates.map((a) => (
            <div
              key={a.id}
              className="bg-card border border-border rounded-xl p-4 flex items-center gap-3 hover:bg-muted/30 transition-colors"
            >
              <button
                className="flex-1 min-w-0 text-left"
                onClick={() => a.id && setDetailId(a.id)}
              >
                <div className="flex items-center gap-2">
                  <span className={`text-xs px-2 py-0.5 rounded-full ${a.status === "active" ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"}`}>
                    {a.status}
                  </span>
                  {a.featured && <span className="text-xs px-2 py-0.5 rounded-full bg-primary/15 text-primary">Featured</span>}
                  <span className="text-xs text-muted-foreground">{a.category}</span>
                </div>
                <h4 className="font-semibold truncate">{a.name}</h4>
                {a.tagline && <p className="text-xs text-muted-foreground truncate">{a.tagline}</p>}
              </button>
              <Button variant="ghost" size="sm" onClick={() => setEditing(a)}><Pencil className="h-4 w-4" /></Button>
              <Button variant="ghost" size="sm" className="text-destructive" onClick={() => remove(a.id)}><Trash2 className="h-4 w-4" /></Button>
            </div>
          ))}
          {affiliates.length === 0 && <p className="text-sm text-muted-foreground">No affiliates yet.</p>}
        </div>
      </TabsContent>

      <TabsContent value="applications">
        <div className="space-y-3">
          {applications.map((app) => (
            <div key={app.id} className="bg-card border border-border rounded-xl p-4">
              <div className="flex items-start justify-between gap-3 mb-2">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs px-2 py-0.5 rounded-full bg-muted text-muted-foreground">{app.status}</span>
                    <span className="text-xs text-muted-foreground">{app.category}</span>
                  </div>
                  <h4 className="font-semibold">{app.applicant_name} {app.organization && <span className="text-muted-foreground font-normal">· {app.organization}</span>}</h4>
                  <p className="text-xs text-muted-foreground">{app.email}{app.location ? ` · ${app.location}` : ""}</p>
                </div>
                <div className="flex gap-1 shrink-0">
                  <Button size="sm" variant="ghost" className="text-primary" onClick={() => approve(app.id)} title="Approve & list"><Check className="h-4 w-4" /></Button>
                  <Button size="sm" variant="ghost" className="text-destructive" onClick={() => setAppStatus(app.id, "declined")} title="Decline"><X className="h-4 w-4" /></Button>
                </div>
              </div>
              <p className="text-sm text-muted-foreground whitespace-pre-line">{app.pitch}</p>
              {app.services?.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {app.services.map((s) => <span key={s} className="text-[11px] bg-muted text-muted-foreground px-2 py-0.5 rounded-full">{s}</span>)}
                </div>
              )}
              {app.portfolio_url && <a href={app.portfolio_url} target="_blank" rel="noreferrer" className="text-xs text-primary hover:underline mt-2 inline-block">{app.portfolio_url}</a>}
            </div>
          ))}
          {applications.length === 0 && <p className="text-sm text-muted-foreground">No applications yet.</p>}
        </div>
      </TabsContent>

      <TabsContent value="leads">
        <div className="space-y-3">
          {leads.map((l) => (
            <div key={l.id} className="bg-card border border-border rounded-xl p-4">
              <div className="flex items-start justify-between gap-3 mb-2">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs px-2 py-0.5 rounded-full bg-muted text-muted-foreground">{l.status}</span>
                    {l.affiliates && <span className="text-xs text-primary">→ {l.affiliates.name}</span>}
                  </div>
                  <h4 className="font-semibold">{l.name} {l.organization && <span className="text-muted-foreground font-normal">· {l.organization}</span>}</h4>
                  <p className="text-xs text-muted-foreground">{l.email}{l.budget_range ? ` · ${l.budget_range}` : ""}{l.timeline ? ` · ${l.timeline}` : ""}</p>
                </div>
                <div className="flex gap-1 shrink-0">
                  <Button size="sm" variant="ghost" onClick={() => setLeadStatus(l.id, "contacted")}>Mark contacted</Button>
                  <Button size="sm" variant="ghost" onClick={() => setLeadStatus(l.id, "closed")}>Close</Button>
                </div>
              </div>
              <p className="text-sm text-muted-foreground whitespace-pre-line">{l.project_summary}</p>
            </div>
          ))}
          {leads.length === 0 && <p className="text-sm text-muted-foreground">No leads yet.</p>}
        </div>
      </TabsContent>

      <TabsContent value="performance">
        <AffiliatePerformanceTable
          affiliates={affiliates as any}
          leads={leads as any}
          onOpen={(id) => setDetailId(id)}
        />
      </TabsContent>
    </Tabs>
  );
};

export default AffiliatesAdminTab;